import {PreventionInvalidityCode} from "../preventative-interactions";
import {buildRelationTypeIdByTitleKey, normalizeRelationTitleKey, resolveRelationTitleFromCell} from "./lookup-stores/relation-title-lookup";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Assigns product relations: source part → related part via relation title.
 */
export class ProductRelationAssignmentImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {{ allProductRelationTypes?: Array<{ id: *, title?: string }> }} [options]
     */
    async run(mappedData, isFinalRun = false, options = {}) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];
        const relationTypeIdByTitleKey = buildRelationTypeIdByTitleKey(options.allProductRelationTypes);

        /** @type {{index: number, item: *, sourcePartNoUpper: string, relationPartNoUpper: string, relationTitleTrimmed: string, relationTitleKey: string, codes: string[], invalidPartsMeta: Record<string, boolean>}[]} */
        const rowState = rows.map((item, index) => {
            const relationTitleTrimmed = resolveRelationTitleFromCell(item?.relationTitle);
            return {
                index,
                item,
                sourcePartNoUpper: toPartNumberUpper(item?.sourcePartNumber),
                relationPartNoUpper: toPartNumberUpper(item?.relationPartNumber),
                relationTitleTrimmed,
                relationTitleKey: normalizeRelationTitleKey(relationTitleTrimmed),
                codes: [],
                invalidPartsMeta: {},
            };
        });

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (!rs.sourcePartNoUpper || !rs.relationPartNoUpper || !rs.relationTitleTrimmed) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        const uniqueParts = [
            ...new Set(
                rowState.flatMap((r) => [r.sourcePartNoUpper, r.relationPartNoUpper].filter(Boolean)),
            ),
        ].sort();

        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (rs.sourcePartNoUpper && !partIds[rs.sourcePartNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
                rs.invalidPartsMeta.sourcePartNumber = true;
            }
            if (rs.relationPartNoUpper && !partIds[rs.relationPartNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
                rs.invalidPartsMeta.relationPartNumber = true;
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (
                rs.sourcePartNoUpper &&
                rs.relationPartNoUpper &&
                rs.sourcePartNoUpper === rs.relationPartNoUpper &&
                !rs.invalidPartsMeta.sourcePartNumber &&
                !rs.invalidPartsMeta.relationPartNumber
            ) {
                rs.codes.push(PreventionInvalidityCode.INVALID_RELATION_ASSIGNMENT);
            }
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.relationTitleKey) {
                continue;
            }
            if (!relationTypeIdByTitleKey[rs.relationTitleKey]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_RELATION);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) {
                continue;
            }
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
                meta: Object.keys(rs.invalidPartsMeta).length
                    ? {invalidParts: rs.invalidPartsMeta}
                    : undefined,
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun
                    ? null
                    : {
                        currentPartId: null,
                        relatedPartId: null,
                        relationTypeId: null,
                        sourcePartNumber: null,
                        relationPartNumber: null,
                        relationTitle: null,
                    };
            }

            const rs = rowState[index];
            const sourcePartId = partIds[rs.sourcePartNoUpper];
            const relatedPartId = partIds[rs.relationPartNoUpper];
            const relationTypeId = relationTypeIdByTitleKey[rs.relationTitleKey];
            const relationTitleDisplay =
                typeof item.relationTitle === 'string'
                    ? item.relationTitle
                    : item.relationTitle?.title ?? item.relationTitle;

            if (issueIndexSet.has(index)) {
                return isFinalRun
                    ? null
                    : {
                        currentPartId: sourcePartId ?? null,
                        relatedPartId: relatedPartId ?? null,
                        relationTypeId: relationTypeId ?? null,
                        sourcePartNumber: item.sourcePartNumber,
                        relationPartNumber: item.relationPartNumber,
                        relationTitle: relationTitleDisplay,
                    };
            }

            return {
                currentPartId: sourcePartId,
                relatedPartId,
                relationTypeId,
                sourcePartNumber: toPartNumberUpper(item?.sourcePartNumber),
                relationPartNumber: toPartNumberUpper(item?.relationPartNumber),
                relationTitle: relationTitleDisplay,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
